/**
 * Pending state for one DM bubble's Translate menu.
 *
 * Two clicks before the button disables share one burst. The server dedupes
 * them to a single model call: one response is the finished translation, the
 * other is `{ status: 'pending' }` with an empty body. Clearing a boolean in
 * `finally`, and treating that pending payload as a sticky "Translating…"
 * notice, leaves the menu pending after the translation is already on screen.
 * Whichever response settles last used to write the flag.
 *
 * A burst stays pending until a ready translation is seen or every in-flight
 * request has settled. A later pending payload cannot re-enter that state, and
 * a payload from an older burst is ignored.
 */

export type DmTranslateNotice = 'translating' | 'busy' | 'unavailable' | null;

export interface DmTranslateClickPhase {
  /** Bumped when a click starts while the menu is idle, so late responses die. */
  generation: number;
  inflight: number;
  /** This burst already applied a completed or stale translation. */
  sawReady: boolean;
  busy: boolean;
  notice: DmTranslateNotice;
}

export const initialDmTranslateClick: DmTranslateClickPhase = {
  generation: 0,
  inflight: 0,
  sawReady: false,
  busy: false,
  notice: null,
};

export type DmTranslateClickEvent =
  | { type: 'start' }
  | { type: 'ready'; generation: number }
  | { type: 'pending'; generation: number }
  | { type: 'miss'; generation: number }
  | { type: 'error'; generation: number; notice: 'busy' | 'unavailable' }
  | { type: 'translation-arrived'; generation: number };

/** The Translate summary shows "Translating…" while a request is running or handed off. */
export function dmTranslateMenuPending(phase: DmTranslateClickPhase): boolean {
  return phase.busy || phase.notice === 'translating';
}

/** Map a translateMessagePart status onto the burst. `original` and anything else are misses. */
export function dmTranslateClickEventForResult(
  generation: number,
  status: string | null | undefined
):
  | { type: 'ready'; generation: number }
  | { type: 'pending'; generation: number }
  | { type: 'miss'; generation: number } {
  if (status === 'completed' || status === 'stale') return { type: 'ready', generation };
  if (status === 'pending') return { type: 'pending', generation };
  return { type: 'miss', generation };
}

function sameBurst(phase: DmTranslateClickPhase, generation: number): boolean {
  return generation === phase.generation;
}

export function reduceDmTranslateClick(
  phase: DmTranslateClickPhase,
  event: DmTranslateClickEvent
): DmTranslateClickPhase {
  switch (event.type) {
    case 'start': {
      if (!phase.busy) {
        return {
          generation: phase.generation + 1,
          inflight: 1,
          sawReady: false,
          busy: true,
          notice: null,
        };
      }
      return { ...phase, inflight: phase.inflight + 1, busy: true, notice: null };
    }
    case 'ready': {
      if (!sameBurst(phase, event.generation)) return phase;
      return {
        ...phase,
        inflight: Math.max(0, phase.inflight - 1),
        sawReady: true,
        busy: false,
        notice: null,
      };
    }
    case 'pending': {
      if (!sameBurst(phase, event.generation)) return phase;
      const inflight = Math.max(0, phase.inflight - 1);
      if (phase.sawReady) return { ...phase, inflight, busy: false, notice: null };
      if (inflight > 0) return { ...phase, inflight, busy: true, notice: null };
      return { ...phase, inflight, busy: false, notice: 'translating' };
    }
    case 'miss': {
      if (!sameBurst(phase, event.generation)) return phase;
      const inflight = Math.max(0, phase.inflight - 1);
      if (phase.sawReady) return { ...phase, inflight, busy: false, notice: null };
      return { ...phase, inflight, busy: inflight > 0, notice: null };
    }
    case 'error': {
      if (!sameBurst(phase, event.generation)) return phase;
      const inflight = Math.max(0, phase.inflight - 1);
      if (phase.sawReady) return { ...phase, inflight, busy: false, notice: null };
      if (inflight > 0) return { ...phase, inflight, busy: true, notice: null };
      return { ...phase, inflight, busy: false, notice: event.notice };
    }
    case 'translation-arrived': {
      if (!sameBurst(phase, event.generation)) return phase;
      return { ...phase, sawReady: true, busy: false, notice: null };
    }
  }
}
